import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { theme } from './theme/theme';
import { Layout } from './components/layout/Layout';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { RangeOfficerCourseRoute } from './components/auth/RangeOfficerCourseRoute';
import { RoleProtectedRoute } from './components/auth/RoleProtectedRoute';

// Lazy load all pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const RulesPage = lazy(() => import('./pages/RulesPage').then(m => ({ default: m.RulesPage })));
const GalleryPage = lazy(() => import('./pages/GalleryPage').then(m => ({ default: m.GalleryPage })));
const NationalCouncilPage = lazy(() => import('./pages/NationalCouncilPage').then(m => ({ default: m.NationalCouncilPage })));
const CalendarPage = lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const HonourRollPage = lazy(() => import('./pages/HonourRollPage').then(m => ({ default: m.HonourRollPage })));
const EventsPage = lazy(() => import('./pages/EventsPage').then(m => ({ default: m.EventsPage })));
const EventRegistrationPage = lazy(() => import('./pages/EventRegistrationPage').then(m => ({ default: m.EventRegistrationPage })));
const EventRsvpPage = lazy(() => import('./pages/EventRsvpPage').then(m => ({ default: m.EventRsvpPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('./pages/SignupPage').then(m => ({ default: m.SignupPage })));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const MembershipPage = lazy(() => import('./pages/MembershipPage').then(m => ({ default: m.MembershipPage })));
const MembershipApplicationPage = lazy(() => import('./pages/MembershipApplicationPage').then(m => ({ default: m.MembershipApplicationPage })));
const MembershipRenewalPage = lazy(() => import('./pages/MembershipRenewalPage').then(m => ({ default: m.MembershipRenewalPage })));
const MembershipPaymentResultPage = lazy(() => import('./pages/MembershipPaymentResultPage').then(m => ({ default: m.MembershipPaymentResultPage })));

// Member pages
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const MemberEventsPage = lazy(() => import('./pages/MemberEventsPage').then(m => ({ default: m.MemberEventsPage })));
const MemberScoresPage = lazy(() => import('./pages/MemberScoresPage'));
const MemberActivityPage = lazy(() => import('./pages/MemberActivityPage').then(m => ({ default: m.MemberActivityPage })));
const MemberCoursesPage = lazy(() => import('./pages/MemberCoursesPage').then(m => ({ default: m.MemberCoursesPage })));
const RangeOfficerCoursePage = lazy(() => import('./pages/RangeOfficerCoursePage').then(m => ({ default: m.RangeOfficerCoursePage })));
const RangeOfficerCourseLessonPage = lazy(() => import('./pages/RangeOfficerCourseLessonPage').then(m => ({ default: m.RangeOfficerCourseLessonPage })));
const RangeOfficerCourseExamPage = lazy(() => import('./pages/RangeOfficerCourseExamPage').then(m => ({ default: m.RangeOfficerCourseExamPage })));
const RangeOfficerCourseResultPage = lazy(() => import('./pages/RangeOfficerCourseResultPage').then(m => ({ default: m.RangeOfficerCourseResultPage })));

// Admin pages
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const AdminMembersPage = lazy(() => import('./pages/AdminMembersPage').then(m => ({ default: m.AdminMembersPage })));
const AdminCalendarPage = lazy(() => import('./pages/AdminCalendarPage').then(m => ({ default: m.AdminCalendarPage })));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage').then(m => ({ default: m.AdminSettingsPage })));
const AdminMemberFeesPage = lazy(() => import('./pages/AdminMemberFeesPage').then(m => ({ default: m.AdminMemberFeesPage })));
const AdminGalleryPage = lazy(() => import('./pages/AdminGalleryPage').then(m => ({ default: m.AdminGalleryPage })));
const AdminHomeBannersPage = lazy(() => import('./pages/AdminHomeBannersPage').then(m => ({ default: m.AdminHomeBannersPage })));
const AdminRangeOfficerPage = lazy(() => import('./pages/AdminRangeOfficerPage').then(m => ({ default: m.AdminRangeOfficerPage })));
const AdminMembershipCertificatePage = lazy(() => import('./pages/AdminMembershipCertificatePage').then(m => ({ default: m.AdminMembershipCertificatePage })));
const AdminEventsPage = lazy(() => import('./pages/AdminEventsPage').then(m => ({ default: m.AdminEventsPage })));
const AdminEventDetailPage = lazy(() => import('./pages/AdminEventDetailPage').then(m => ({ default: m.AdminEventDetailPage })));
const AdminEventInvitationsPage = lazy(() => import('./pages/AdminEventInvitationsPage').then(m => ({ default: m.AdminEventInvitationsPage })));
const AdminShootingDisciplinesPage = lazy(() => import('./pages/AdminShootingDisciplinesPage').then(m => ({ default: m.AdminShootingDisciplinesPage })));
const AdminCourseOfFirePage = lazy(() => import('./pages/AdminCourseOfFirePage').then(m => ({ default: m.AdminCourseOfFirePage })));
const AdminScoringPage = lazy(() => import('./pages/AdminScoringPage').then(m => ({ default: m.AdminScoringPage })));
const StatsOfficeScoringPage = lazy(() => import('./pages/StatsOfficeScoringPage').then(m => ({ default: m.StatsOfficeScoringPage })));
const AdminRankingsPage = lazy(() => import('./pages/AdminRankingsPage').then(m => ({ default: m.AdminRankingsPage })));
const AdminEventStaffPage = lazy(() => import('./pages/AdminEventStaffPage').then(m => ({ default: m.AdminEventStaffPage })));
const AdminManualScoresPage = lazy(() => import('./pages/AdminManualScoresPage').then(m => ({ default: m.AdminManualScoresPage })));
const AdminManualScoresImportPage = lazy(() => import('./pages/AdminManualScoresImportPage').then(m => ({ default: m.AdminManualScoresImportPage })));
const AdminManualScoresRankingsPage = lazy(() => import('./pages/AdminManualScoresRankingsPage').then(m => ({ default: m.AdminManualScoresRankingsPage })));
const AdminManualScoresCompetitionPage = lazy(() => import('./pages/AdminManualScoresCompetitionPage').then(m => ({ default: m.AdminManualScoresCompetitionPage })));
const AdminManualScoresAllPage = lazy(() => import('./pages/AdminManualScoresAllPage'));
const AdminManualScoresCompetitorReportPage = lazy(() => import('./pages/AdminManualScoresCompetitorReportPage'));
const AdminManualScoresEntryPage = lazy(() => import('./pages/AdminManualScoresEntryPage'));
const AdminProvincesPage = lazy(() => import('./pages/AdminProvincesPage').then(m => ({ default: m.AdminProvincesPage })));
const AdminClubsPage = lazy(() => import('./pages/AdminClubsPage').then(m => ({ default: m.AdminClubsPage })));
const AdminTargetOrdersPage = lazy(() => import('./pages/AdminTargetOrdersPage').then(m => ({ default: m.AdminTargetOrdersPage })));
const AdminTargetOrderDetailPage = lazy(() => import('./pages/AdminTargetOrderDetailPage').then(m => ({ default: m.AdminTargetOrderDetailPage })));
const AdminClubStatementsPage = lazy(() => import('./pages/AdminClubStatementsPage').then(m => ({ default: m.AdminClubStatementsPage })));
const AdminTargetCataloguePage = lazy(() => import('./pages/AdminTargetCataloguePage').then(m => ({ default: m.AdminTargetCataloguePage })));
const LiveScoringPage = lazy(() => import('./pages/LiveScoringPage').then(m => ({ default: m.LiveScoringPage })));
const EventScoresPage = lazy(() => import('./pages/EventScoresPage').then(m => ({ default: m.EventScoresPage })));
const EventFinalResultsPage = lazy(() => import('./pages/EventFinalResultsPage').then(m => ({ default: m.EventFinalResultsPage })));
const EventFinalScoresPage = lazy(() => import('./pages/EventFinalScoresPage').then(m => ({ default: m.EventFinalScoresPage })));
const RangeRegisterPage = lazy(() => import('./pages/RangeRegisterPage').then(m => ({ default: m.RangeRegisterPage })));
const EventCourseOfFirePage = lazy(() => import('./pages/EventCourseOfFirePage').then(m => ({ default: m.EventCourseOfFirePage })));
const MyEventScoresPage = lazy(() => import('./pages/MyEventScoresPage').then(m => ({ default: m.MyEventScoresPage })));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage').then(m => ({ default: m.LeaderboardPage })));

// Buddy Scoring pages
const BuddyScoringDashboardPage = lazy(() => import('./pages/BuddyScoringDashboardPage').then(m => ({ default: m.BuddyScoringDashboardPage })));
const BuddyScoringPage = lazy(() => import('./pages/BuddyScoringPage').then(m => ({ default: m.BuddyScoringPage })));
const BuddyScoreAcceptancePage = lazy(() => import('./pages/BuddyScoreAcceptancePage').then(m => ({ default: m.BuddyScoreAcceptancePage })));

// Event Staff Setup
const StaffSetupPage = lazy(() => import('./pages/StaffSetupPage').then(m => ({ default: m.StaffSetupPage })));

// Visitor Setup
const VisitorSetupPage = lazy(() => import('./pages/VisitorSetupPage').then(m => ({ default: m.VisitorSetupPage })));
const PhotoCapturePage = lazy(() => import('./pages/PhotoCapturePage').then(m => ({ default: m.PhotoCapturePage })));
const ScoreAssistantPage = lazy(() => import('./pages/ScoreAssistantPage').then(m => ({ default: m.ScoreAssistantPage })));
const BuddyTestLoginPage = lazy(() => import('./pages/BuddyTestLoginPage').then(m => ({ default: m.BuddyTestLoginPage })));

// Loading component
const PageLoader = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '60vh',
      flexDirection: 'column',
      gap: 2
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

// Smart Dashboard Router - directs to appropriate dashboard based on user type
const SmartDashboard = lazy(() => import('./components/auth/SmartDashboard'));

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public pages */}
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/about" element={<Layout><AboutPage /></Layout>} />
              <Route path="/rules" element={<Layout><RulesPage /></Layout>} />
              <Route path="/gallery" element={<Layout><GalleryPage /></Layout>} />
              <Route path="/calendar" element={<Layout><CalendarPage /></Layout>} />
              <Route path="/honour-roll" element={<Layout><HonourRollPage /></Layout>} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:eventId" element={<EventRegistrationPage />} />
              <Route path="/events/:eventId/register" element={<EventRegistrationPage />} />
              <Route path="/events/:eventId/rsvp" element={<EventRsvpPage />} />
              <Route path="/events/:eventId/scoring" element={<LiveScoringPage />} />
              <Route path="/events/:eventId/results" element={<EventScoresPage />} />
              <Route path="/events/:eventId/final-results" element={<EventFinalResultsPage />} />
              <Route path="/events/:eventId/range-register" element={<RangeRegisterPage />} />
              <Route path="/events/:eventId/course-of-fire" element={<EventCourseOfFirePage />} />
              <Route path="/events/:eventId/course-of-fire/:eventNumber" element={<EventCourseOfFirePage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/council" element={<Layout><NationalCouncilPage /></Layout>} />
              <Route path="/membership" element={<MembershipPage />} />
              <Route path="/staff-setup/:staffId/:token" element={<StaffSetupPage />} />
              <Route path="/visitor-setup/:visitorId/:token" element={<VisitorSetupPage />} />
              <Route path="/capture/:token" element={<PhotoCapturePage />} />
              <Route path="/buddy-test-login" element={<BuddyTestLoginPage />} />
              <Route path="/login" element={<Layout><LoginPage /></Layout>} />
              <Route path="/signup" element={<Layout><SignupPage /></Layout>} />
              <Route path="/renew/:token" element={<MembershipRenewalPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route 
                path="/membership-application" 
                element={
                  <ProtectedRoute>
                    <MembershipApplicationPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Member/Visitor Dashboard */}
              <Route
                path="/membership/payment-result"
                element={
                  <ProtectedRoute>
                    <MembershipPaymentResultPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/membership/renew"
                element={
                  <ProtectedRoute>
                    <MembershipRenewalPage />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <SmartDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/documents" 
                element={
                  <ProtectedRoute>
                    <DocumentsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/member/events" 
                element={
                  <ProtectedRoute>
                    <MemberEventsPage />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/member/scores"
                element={
                  <ProtectedRoute>
                    <MemberScoresPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/member/activity"
                element={
                  <ProtectedRoute>
                    <MemberActivityPage />
                  </ProtectedRoute>
                }
              />

              {/* Online Courses */}
              <Route
                path="/member/courses"
                element={
                  <RangeOfficerCourseRoute>
                    <MemberCoursesPage />
                  </RangeOfficerCourseRoute>
                }
              />
              <Route
                path="/member/courses/range-officer"
                element={
                  <RangeOfficerCourseRoute>
                    <RangeOfficerCoursePage />
                  </RangeOfficerCourseRoute>
                }
              />
              <Route
                path="/member/courses/range-officer/exam"
                element={
                  <RangeOfficerCourseRoute>
                    <RangeOfficerCourseExamPage />
                  </RangeOfficerCourseRoute>
                }
              />
              <Route
                path="/member/courses/range-officer/result/:attemptId"
                element={
                  <RangeOfficerCourseRoute>
                    <RangeOfficerCourseResultPage />
                  </RangeOfficerCourseRoute>
                }
              />
              <Route
                path="/member/courses/range-officer/:moduleId"
                element={
                  <RangeOfficerCourseRoute>
                    <RangeOfficerCourseLessonPage />
                  </RangeOfficerCourseRoute>
                }
              />

              {/* Shooter's own event scores + disputes */}
              <Route
                path="/my-scores"
                element={
                  <ProtectedRoute>
                    <MyEventScoresPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/:eventId/my-scores"
                element={
                  <ProtectedRoute>
                    <MyEventScoresPage />
                  </ProtectedRoute>
                }
              />
              {/* Final scores, published once the event is completed */}
              <Route
                path="/events/:eventId/final-scores"
                element={
                  <ProtectedRoute>
                    <EventFinalScoresPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events/:eventId/score-assistant"
                element={
                  <ProtectedRoute>
                    <ScoreAssistantPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Buddy Scoring pages */}
              <Route 
                path="/buddy-scoring" 
                element={
                  <ProtectedRoute>
                    <BuddyScoringDashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/buddy-scoring/:eventId/:disciplineEventNumber/score" 
                element={
                  <ProtectedRoute>
                    <BuddyScoringPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/buddy-scoring/:eventId/:disciplineEventNumber/accept/:scoreCardId" 
                element={
                  <ProtectedRoute>
                    <BuddyScoreAcceptancePage />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/buddy-scoring/:eventId"
                element={
                  <ProtectedRoute>
                    <BuddyScoringDashboardPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Admin pages - Role-based access control */}
              {/* Dashboard - accessible to super_admin, members_admin only */}
              <Route 
                path="/admin" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'members_admin']}>
                    <AdminPage />
                  </RoleProtectedRoute>
                } 
              />
              {/* Members - accessible to super_admin, members_admin, regional_admin (view-only, region-scoped) */}
              <Route 
                path="/admin/members" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'members_admin', 'regional_admin']}>
                    <AdminMembersPage />
                  </RoleProtectedRoute>
                } 
              />
              {/* Calendar - accessible to super_admin, regional_admin, event_manager, club_admin */}
              <Route 
                path="/admin/calendar" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'regional_admin', 'event_manager', 'club_admin']}>
                    <AdminCalendarPage />
                  </RoleProtectedRoute>
                } 
              />
              <Route
                path="/admin/member-fees"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'members_admin']}>
                    <AdminMemberFeesPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Settings - accessible to super_admin only */}
              <Route 
                path="/admin/settings" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminSettingsPage />
                  </RoleProtectedRoute>
                } 
              />
              {/* Photo library - super_admin, regional_admin, and event_manager can contribute; rename and delete stay super_admin */}
              <Route
                path="/admin/gallery"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'regional_admin', 'event_manager']}>
                    <AdminGalleryPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/home-banners"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminHomeBannersPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Range Officer - accessible to super_admin, members_admin, regional_admin */}
              <Route 
                path="/admin/range-officer" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'members_admin', 'regional_admin']}>
                    <AdminRangeOfficerPage />
                  </RoleProtectedRoute>
                } 
              />
              {/* Membership Certificate - accessible to super_admin, members_admin, regional_admin */}
              <Route 
                path="/admin/membership-certificate" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'members_admin', 'regional_admin']}>
                    <AdminMembershipCertificatePage />
                  </RoleProtectedRoute>
                } 
              />
              {/* Events - accessible to super_admin, event_manager, regional_admin, event_staff */}
              <Route 
                path="/admin/events" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'event_manager', 'regional_admin', 'event_staff', 'club_admin']}>
                    <AdminEventsPage />
                  </RoleProtectedRoute>
                } 
              />
              <Route 
                path="/admin/events/:eventId" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'event_manager', 'regional_admin', 'event_staff', 'club_admin']}>
                    <AdminEventDetailPage />
                  </RoleProtectedRoute>
                } 
              />
              <Route 
                path="/admin/events/:eventId/invitations" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'event_manager', 'regional_admin', 'event_staff', 'club_admin']}>
                    <AdminEventInvitationsPage />
                  </RoleProtectedRoute>
                } 
              />
              {/* Disciplines - accessible to super_admin */}
              <Route 
                path="/admin/disciplines" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminShootingDisciplinesPage />
                  </RoleProtectedRoute>
                } 
              />
              {/* Course of Fire - accessible to super_admin */}
              <Route 
                path="/admin/course-of-fire" 
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminCourseOfFirePage />
                  </RoleProtectedRoute>
                } 
              />
              {/* Manual Scoring - accessible to super_admin, event_manager, club_admin */}
              <Route
                path="/admin/scoring"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'event_manager', 'club_admin']}>
                    <AdminScoringPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Stats Office - accessible to super_admin, event_manager, regional_admin, event_staff */}
              <Route
                path="/admin/stats-office"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'event_manager', 'regional_admin', 'event_staff', 'club_admin']}>
                    <StatsOfficeScoringPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Rankings - accessible to super_admin, members_admin */}
              <Route
                path="/admin/rankings"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'members_admin']}>
                    <AdminRankingsPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Event Staff - accessible to super_admin, regional_admin */}
              <Route
                path="/admin/event-staff"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'regional_admin']}>
                    <AdminEventStaffPage />
                  </RoleProtectedRoute>
                }
              />
              {/* Interim manual scores subsystem */}
              <Route
                path="/admin/manual-scores"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminManualScoresPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/manual-scores/import"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminManualScoresImportPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/manual-scores/rankings"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminManualScoresRankingsPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/manual-scores/competition"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminManualScoresCompetitionPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/manual-scores/all"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminManualScoresAllPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/manual-scores/competitor-report"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminManualScoresCompetitorReportPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/manual-scores/entry"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminManualScoresEntryPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/provinces"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminProvincesPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/clubs"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'regional_admin', 'club_admin', 'event_manager']}>
                    <AdminClubsPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/target-orders"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'regional_admin', 'event_manager', 'club_admin']}>
                    <AdminTargetOrdersPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/target-orders/new"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'regional_admin', 'event_manager', 'club_admin']}>
                    <AdminTargetOrderDetailPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/target-orders/:orderId"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'regional_admin', 'event_manager', 'club_admin']}>
                    <AdminTargetOrderDetailPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/club-statements"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin', 'regional_admin', 'event_manager', 'club_admin']}>
                    <AdminClubStatementsPage />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="/admin/target-catalogue"
                element={
                  <RoleProtectedRoute allowedRoles={['super_admin']}>
                    <AdminTargetCataloguePage />
                  </RoleProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
          <PWAInstallPrompt />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Add error boundary for debugging
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
});

export default App;
